import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Video, Loader2, RefreshCw, Trash2, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  adminListCloudinaryCleanupFailures,
  adminRetryCloudinaryCleanup,
  adminDismissCloudinaryCleanup,
} from '../lib/api';
import { useConfirmDialog } from '../components/ConfirmDialog';

/**
 * Admin Cloudinary Cleanup Failures page (`/admin/cloudinary-cleanup`).
 *
 * When a prayer is hard-deleted, the backend best-effort destroys its
 * associated Cloudinary video. If that destroy call fails (credentials
 * missing, network hiccup, Cloudinary API blip, etc.), a row is written
 * to the `cloudinary_cleanup_failures` collection so admins can:
 *   * Retry the destroy call (`POST .../{prayer_id}/retry`)
 *   * Dismiss the entry if they cleaned it up out-of-band (`DELETE`)
 *   * Open the Cloudinary URL directly to prune manually
 */
const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const AdminCloudinaryCleanup = () => {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [lastAction, setLastAction] = useState(null); // { prayerId, kind: 'retry-ok' | 'retry-fail' | 'dismissed', msg? }
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const data = await adminListCloudinaryCleanupFailures();
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load cleanup failures');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  const doRetry = async (row) => {
    setBusyId(row.prayer_id); setLastAction(null);
    try {
      const res = await adminRetryCloudinaryCleanup(row.prayer_id);
      if (res?.ok && res?.resolved) {
        setRows((prev) => prev.filter((r) => r.prayer_id !== row.prayer_id));
        setLastAction({ prayerId: row.prayer_id, kind: 'retry-ok' });
      } else {
        setLastAction({ prayerId: row.prayer_id, kind: 'retry-fail', msg: res?.error || 'Retry failed' });
        // Refresh the row's last_error/failed_at from the server.
        await load();
      }
    } catch (e) {
      setLastAction({ prayerId: row.prayer_id, kind: 'retry-fail', msg: e?.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
    }
  };

  const doDismiss = async (row) => {
    const ok = await confirm({
      title: 'Dismiss this cleanup entry?',
      description: 'Only do this if you already deleted the video from Cloudinary. The entry will be removed from this list.',
      confirmText: 'Dismiss',
      cancelText: 'Cancel',
      destructive: true,
      preview: { label: 'Orphan video', items: [row.public_id || row.video_url || row.prayer_id] },
    });
    if (!ok) return;
    setBusyId(row.prayer_id); setLastAction(null);
    try {
      await adminDismissCloudinaryCleanup(row.prayer_id);
      setRows((prev) => prev.filter((r) => r.prayer_id !== row.prayer_id));
      setLastAction({ prayerId: row.prayer_id, kind: 'dismissed' });
    } catch (e) {
      setLastAction({ prayerId: row.prayer_id, kind: 'retry-fail', msg: e?.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
    }
  };

  if (authLoading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /> Loading…</div>;
  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="admin-cloudinary-cleanup-page">
      {confirmDialog}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Video size={22} className="text-rose-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-cloudinary-cleanup-title">Video Cleanup Log</h1>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-500" data-testid="admin-cleanup-count">
            {rows.length} orphan{rows.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="refresh-cleanup-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      <p className="text-sm text-zinc-500 mb-4">
        Cloudinary videos whose auto-deletion failed during a prayer hard-delete. Retry the destroy,
        or dismiss the entry after cleaning up manually in the Cloudinary console.
      </p>

      {err && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4" data-testid="admin-cleanup-error">
          {err}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="p-10 text-center text-zinc-500">
          <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="mt-8 mx-auto max-w-lg rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/60 shadow-sm px-8 py-10 flex flex-col items-center text-center"
          data-testid="admin-cleanup-empty"
        >
          <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 dark:text-emerald-300 mb-4">
            <CheckCircle2 size={26} strokeWidth={2.25} />
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
            No orphan videos
          </h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Every deletion cleaned up successfully.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-cleanup-list">
          {rows.map((r) => {
            const isBusy = busyId === r.prayer_id;
            const action = lastAction && lastAction.prayerId === r.prayer_id ? lastAction : null;
            return (
              <li
                key={r.prayer_id}
                data-testid={`cleanup-row-${r.prayer_id}`}
                className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
              >
                <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                  <div className="w-9 h-9 rounded-full bg-rose-100 dark:bg-rose-950/50 flex items-center justify-center text-rose-600 dark:text-rose-300 flex-shrink-0">
                    <AlertTriangle size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-zinc-900 dark:text-zinc-100 truncate" title={r.prayer_title || r.prayer_id}>
                      {r.prayer_title || <span className="italic text-zinc-500">Untitled prayer</span>}
                    </div>
                    <div className="text-xs text-zinc-500 truncate mt-0.5" title={r.public_id}>
                      public_id: <span className="font-mono">{r.public_id || '—'}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      Failed {fmt(r.failed_at)}
                    </div>
                    {r.last_error && (
                      <div className="text-xs text-rose-600 dark:text-rose-400 mt-1 break-words">
                        {r.last_error}
                      </div>
                    )}
                    {r.video_url && (
                      <a
                        href={r.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`cleanup-open-${r.prayer_id}`}
                        className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 hover:underline mt-1"
                      >
                        <ExternalLink size={11} /> Open on Cloudinary
                      </a>
                    )}
                    {action && (
                      <div className={`text-xs mt-2 ${action.kind === 'retry-ok' || action.kind === 'dismissed' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {action.kind === 'retry-ok' && 'Cleanup succeeded.'}
                        {action.kind === 'dismissed' && 'Entry dismissed.'}
                        {action.kind === 'retry-fail' && `Retry failed: ${action.msg}`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-end">
                    <button
                      type="button"
                      onClick={() => doRetry(r)}
                      disabled={isBusy}
                      data-testid={`cleanup-retry-${r.prayer_id}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
                    >
                      {isBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      Retry
                    </button>
                    <button
                      type="button"
                      onClick={() => doDismiss(r)}
                      disabled={isBusy}
                      data-testid={`cleanup-dismiss-${r.prayer_id}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-60"
                    >
                      <Trash2 size={12} />
                      Dismiss
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AdminCloudinaryCleanup;
